<template>
    <!-- ITDEVELOP-2722: ui/Dialog replaces the hand-rolled native <dialog> +
         manual outside-click/Escape/open-state plumbing (see feedback-modal). -->
    <Dialog :open="showModal" @update:open="(open) => !open && closeModal()">
        <DialogContent
            name="help feedback view"
            size="lg"
            class="max-h-[90vh] max-w-none md:w-1/2"
            :aria-describedby="undefined"
        >
            <DialogTitle class="text-2xl">How can we help you?</DialogTitle>
            <div class="gap-sm flex flex-col">
                <p v-if="showCustomerHelpCopy">
                    We love helping people! Our team is available 24/7 to assist you with your questions or concerns
                    and dedicated to ensuring you have the best experience possible. Please let us know how we can
                    assist you today.
                </p>
                <p v-else>
                    We love helping people! Our team is dedicated to ensuring you have the best experience possible.
                    Please let us know how we can assist you today.
                </p>
            </div>
            <div class="gap-xs flex flex-col">
                <div class="gap-xs flex">
                    <span class="text-error">*</span>
                    <label for="help-input">Message</label>
                </div>
                <div class="input-wrapper" :class="{ 'input-error': errorPool.helpInput }">
                    <textarea
                        id="help-input"
                        v-model="formData.helpInput"
                        rows="4"
                        placeholder="Type your message here..."
                        auto-focus
                    ></textarea>
                </div>
                <p v-if="errorPool.helpInput" class="text-error">
                    {{ errorPool.helpInput }}
                </p>
            </div>
            <div class="gap-xs flex flex-col">
                <label>Attachments</label>
                <FileUpload
                    v-model:uploaded-files="formData.attachments"
                    @upload="setAttachments"
                    @remove="formData.attachments.splice($event, 1)"
                />
            </div>

            <div class="gap-xs flex flex-col">
                <p v-if="showCustomerHelpCopy">
                    When you submit this form, our support team will receive a ticket and aim to respond within 24
                    hours.
                </p>
                <p v-else>
                    By submitting this form our support team will receive a ticket and respond within 24 hours.
                </p>
                <p v-if="showCustomerHelpCopy">
                    For time-sensitive service issues, incident escalations or if you need immediate follow-up on an
                    open ticket, please call
                    <strong class="font-semibold">atNorth Nordic Operation Center (NOC):</strong>
                    <a class="pl-xs text-base underline" href="tel:+468920019"> +46 8 920019</a>.
                </p>
            </div>
            <DialogFooter class="sm:justify-center">
                <Button @click="handleSubmit" @keydown.enter="handleSubmit">
                    <span v-if="!submitting">Submit</span>
                    <i v-else class="i-spinner-white animate-spin" />
                </Button>
                <Button variant="secondary" @click="closeModal">Cancel</Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogTitle } from '@/components/ui/dialog';
import FileUpload from '@/components/support/file-upload.vue';
import { useSupportStore } from '@/stores/support';
import { useOrganizationStore } from '@/stores/organization';
import { useAdminStore } from '@/stores/admin';
import { shouldShowCustomerHelpCopy } from './help-feedback-modal.audience';
import { getDefaultErrorMessage } from '@/lib/http-error';

const supportStore = useSupportStore();
const organizationStore = useOrganizationStore();
const adminStore = useAdminStore();
const submitting = ref(false);
const errorPool = ref<Record<string, string>>({});
const showCustomerHelpCopy = computed(() => shouldShowCustomerHelpCopy(adminStore.userCPRoles));

const emit = defineEmits(['close']);
const formData = ref({
    helpInput: '',
    attachments: [] as File[],
});

const setAttachments = (files: File[]) => {
    formData.value.attachments = files;
};
defineProps<{
    showModal: boolean;
}>();

const validateForm = () => {
    errorPool.value = {};
    if (!formData.value.helpInput.trim()) {
        errorPool.value.helpInput = 'Message cannot be empty.';
    }
    return Object.keys(errorPool.value).length === 0;
};

const handleSubmit = async () => {
    if (submitting.value) return;
    submitting.value = true;

    if (validateForm() === false) {
        submitting.value = false;
        return;
    }

    // Using the same request type as feedback for now, or a generic one if known.
    // Based on feedback-modal.vue, CP SD id is 74, feedback type is 1083.
    // I'll keep it similar but maybe there's a better one.
    // For now, let's use the same one but with a "Help Request" summary.
    try {
        const response = await supportStore.createRequest(
            '74', // CP SD id
            '1622', // Help Request type id
            {
                summary: `Help Request from ${organizationStore.usersOrganization?.name ?? 'atNorth'}`,
                description: formData.value.helpInput,
            },
            formData.value.attachments,
        );

        if (response) {
            organizationStore.setToastMessage('Help request submitted successfully', 'check');
            closeModal();
            return;
        }
    } catch (error) {
        organizationStore.setToastMessage(getDefaultErrorMessage(error), 'error');
    } finally {
        submitting.value = false;
    }
};

const closeModal = () => {
    errorPool.value = {};
    formData.value = {
        helpInput: '',
        attachments: [],
    };
    submitting.value = false;
    emit('close');
};

</script>


